// src/hooks/useTeams.js - Hook for accessing Microsoft Teams data from TeamsStore
import { useMemo } from 'react';
import useTeamsStore from '../stores/TeamsStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing Microsoft Teams integration data and operations
 *
 * Uses Zustand TeamsStore with individual selectors for optimal React re-rendering.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Teams data, operations, and computed values
 */
export const useTeams = () => {
  const socket = useSocket();

  // 🚨 Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change

  // Connection status
  const teamsConnection = useTeamsStore(state => state.teamsConnection);
  const teamsConnectionLoading = useTeamsStore(state => state.teamsConnectionLoading);

  // OAuth flow
  const oauthUrl = useTeamsStore(state => state.oauthUrl);
  const oauthInitiating = useTeamsStore(state => state.oauthInitiating);

  // Disconnection
  const disconnecting = useTeamsStore(state => state.disconnecting);

  // Connection state
  const isConnected = useTeamsStore(state => state.isConnected);

  // ================================
  // OPERATIONS (functions don't change, so we can get them once)
  // ================================

  const loadTeamsConnectionStatus = useTeamsStore(state => state.loadTeamsConnectionStatus);
  const startTeamsOAuth = useTeamsStore(state => state.startTeamsOAuth);
  const disconnectTeams = useTeamsStore(state => state.disconnectTeams);

  // Utility functions
  const getConnectionStatus = useTeamsStore(state => state.getConnectionStatus);
  const isTeamsConnected = useTeamsStore(state => state.isTeamsConnected);
  const isTokenExpired = useTeamsStore(state => state.isTokenExpired);
  const getStatusMessage = useTeamsStore(state => state.getStatusMessage);
  const getAccountEmail = useTeamsStore(state => state.getAccountEmail);
  const hasPermission = useTeamsStore(state => state.hasPermission);

  // Internal initialization state (for fallback check)
  const _initialized = useTeamsStore(state => state._initialized);

  // ================================
  // COMPUTED VALUES (memoized)
  // ================================

  /**
   * Current connection status
   */
  const currentStatus = useMemo(() => {
    return getConnectionStatus();
  }, [getConnectionStatus]);

  /**
   * Status message for UI display
   */
  const statusMessage = useMemo(() => {
    return getStatusMessage();
  }, [getStatusMessage]);

  /**
   * Whether user is currently connected
   */
  const connected = useMemo(() => {
    return isTeamsConnected();
  }, [isTeamsConnected]);

  /**
   * Whether token has expired
   */
  const tokenExpired = useMemo(() => {
    return isTokenExpired();
  }, [isTokenExpired]);

  /**
   * Get connected account email
   */
  const accountEmail = useMemo(() => {
    return getAccountEmail();
  }, [getAccountEmail]);

  /**
   * Check if Teams has required permissions
   */
  const hasTeamsPermission = useMemo(() => {
    return hasPermission();
  }, [hasPermission]);

  /**
   * Check if setup requires action (for OAuth flow)
   */
  const requiresSetup = useMemo(() => {
    return ['disconnected', 'token_expired'].includes(currentStatus);
  }, [currentStatus]);

  /**
   * Check if any operation is loading
   */
  const anyLoading = useMemo(() => {
    return teamsConnectionLoading ||
           oauthInitiating ||
           disconnecting;
  }, [teamsConnectionLoading, oauthInitiating, disconnecting]);

  // ================================
  // FALLBACK INITIALIZATION
  // ================================

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useTeams: Store not initialized at app level, trying fallback');
    socket.initializeStore('TeamsStore');
  }

  // ================================
  // RETURN INTERFACE
  // ================================

  return {
    // Connection status
    teamsConnection,
    teamsConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Account details
    accountEmail,
    hasTeamsPermission,

    // Disconnection
    disconnecting,

    // Computed values
    anyLoading,

    // Operations
    loadTeamsConnectionStatus,
    startTeamsOAuth,
    disconnectTeams,

    // Legacy/convenience aliases
    loading: teamsConnectionLoading
  };
};

export default useTeams;
